"use client";

import { Fragment, type ReactNode } from "react";
import { boardEntryStillCurrentAt, type BoardEntryFreshnessFields } from "@/lib/conditions/board-freshness";
import { useBrowserMinuteClock } from "@/lib/hooks/use-minute-clock";

/**
 * Statically rendered pages (the hub, the homepage, the area pages) present readings judged at
 * their render, and the HTML can be served after a location's local midnight (ISR revalidates on
 * the next visit, and the cron leaves some UTC hours without a run). These gates judge each
 * reading's carried row stamp (lib/conditions/board-freshness.ts) again on the browser's minute
 * clock after hydration, as the board table does: the first client render equals the server's,
 * then a reading whose row has stopped being the location's current score is withdrawn. They only
 * ever withdraw, so a slow device clock cannot revive a reading.
 */

/** The children while the reading is still current on the browser's clock, else the fallback. */
export function WhileScoreCurrent({
  stamp,
  children,
  fallback = null,
}: Readonly<{
  stamp: BoardEntryFreshnessFields | null | undefined;
  children?: ReactNode;
  fallback?: ReactNode;
}>) {
  const nowMs = useBrowserMinuteClock();
  return nowMs === null || boardEntryStillCurrentAt(stamp, nowMs) ? children ?? null : fallback;
}

export type ScoreCurrentItem = Readonly<{
  key: string;
  stamp: BoardEntryFreshnessFields | null;
  node: ReactNode;
}>;

/**
 * A list of server-rendered items, each shown while its reading is still current on the
 * browser's clock; the empty state once none is.
 */
export function ScoreCurrentList({
  items,
  empty,
  className,
  ariaLabel,
}: Readonly<{
  items: readonly ScoreCurrentItem[];
  empty: ReactNode;
  className: string;
  ariaLabel: string;
}>) {
  const nowMs = useBrowserMinuteClock();
  const shown = nowMs === null ? items : items.filter((item) => boardEntryStillCurrentAt(item.stamp, nowMs));
  if (shown.length === 0) return empty;
  return (
    <ul aria-label={ariaLabel} className={className}>
      {shown.map((item) => <Fragment key={item.key}>{item.node}</Fragment>)}
    </ul>
  );
}
